<template>
    <Paginator
        v-bind="primeVueAttrs"
        ref="paginator"
        @page="onPrimeVuePage"
    >
        <template #container="slotProps">
            <div class="primevue-pagination">
                <!-- Page number buttons row -->
                <div class="pagination-pages-row">
                    <e-button
                        v-for="(pageNum, index) in getDisplayedPages(slotProps.page + 1, slotProps.pageCount)"
                        :key="pageNum === '...' ? `ellipsis-${index}` : pageNum"
                        :class="[
                            'pagination-link',
                            { 'is-current': pageNum === slotProps.page + 1 && pageNum !== '...' }
                        ]"
                        :disabled="pageNum === '...'"
                        :aria-label="getAriaLabel(pageNum, slotProps.page + 1)"
                        :aria-current="pageNum === slotProps.page + 1 && pageNum !== '...' ? 'page' : undefined"
                        @click="pageNum !== '...' && handlePageChange(pageNum - 1, slotProps)"
                    >
                        {{ pageNum }}
                    </e-button>
                </div>

                <!-- Navigation row (prev/next) -->
                <div class="pagination-nav-row">
                    <!-- Previous page chevron -->
                    <button
                        class="pagination-chevron pagination-previous"
                        :disabled="slotProps.page === 0"
                        :aria-label="ariaLabels.previous"
                        @click="slotProps.prevPageCallback"
                    >
                        <span class="pagination-icon">
                            <i class="uil uil-angle-left" />
                        </span>
                    </button>

                    <!-- Next page chevron -->
                    <button
                        class="pagination-chevron pagination-next"
                        :disabled="slotProps.page === slotProps.pageCount - 1 || slotProps.pageCount <= 1"
                        :aria-label="ariaLabels.next"
                        @click="slotProps.nextPageCallback"
                    >
                        <span class="pagination-icon">
                            <i class="uil uil-angle-right" />
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </Paginator>
</template>

<script>
import Paginator from 'primevue/paginator';
/**
 * EPagination - Abstraction wrapper around PrimeVue's Paginator
 *
 * Usage:
 *   <e-pagination :total="200" :current="1" :per-page="20">
 *   </e-pagination>
 *
 *   <e-pagination
 *     v-model="currentPage"
 *     :total="totalItems"
 *     :per-page="perPage"
 *     @change="onPageChange">
 *   </e-pagination>
 *
 *   <e-pagination :total="100" :current="5" size="is-small" simple>
 *   </e-pagination>
 *
 * With ARIA labels for accessibility:
 *   <e-pagination
 *     :total="200"
 *     :current="1"
 *     :per-page="20"
 *     aria-page-label="Page"
 *     aria-current-label="Current page"
 *     aria-previous-label="Previous page"
 *     aria-next-label="Next page">
 *   </e-pagination>
 *
 * All Buefy b-pagination props, events, and slots are supported.
 *
 * Buefy to PrimeVue prop mappings:
 *   - total -> totalRecords
 *   - current (1-based) -> first (0-based index)
 *   - per-page -> rows
 *   - aria-page-label -> applied to page number buttons
 *   - aria-current-label -> applied to current page button
 *   - aria-previous-label -> applied to previous chevron button
 *   - aria-next-label -> applied to next chevron button
 */
export default {
    name: 'EPagination',
    components: {
        Paginator,
    },
    inheritAttrs: false,
    data() {
        return {
            isMobile: false,
        };
    },
    computed: {
        ariaLabels() {
            return {
                page: this.$attrs.ariaPageLabel || this.$attrs['aria-page-label'] || 'Page',
                current: this.$attrs.ariaCurrentLabel || this.$attrs['aria-current-label'] || 'Current page',
                previous: this.$attrs.ariaPreviousLabel || this.$attrs['aria-previous-label'] || 'Previous page',
                next: this.$attrs.ariaNextLabel || this.$attrs['aria-next-label'] || 'Next page',
            };
        },
        primeVueAttrs() {
            const mapped = { ...this.$attrs };

            // Map total to totalRecords
            if (mapped.total !== undefined) {
                mapped.totalRecords = mapped.total;
                delete mapped.total;
            }

            // Map per-page to rows (handle both kebab-case and camelCase)
            const perPage = mapped.perPage || mapped['per-page'] || 20;
            mapped.rows = perPage;
            delete mapped.perPage;
            delete mapped['per-page'];

            // Map current (1-based) to first (0-based index)
            const currentPage = mapped.current || mapped.modelValue || 1;
            mapped.first = (currentPage - 1) * perPage;
            delete mapped.current;

            // Remove Buefy-specific props that don't have PrimeVue equivalents
            delete mapped.size;
            delete mapped.rounded;
            delete mapped.simple;
            delete mapped.order;
            delete mapped.iconPack;
            delete mapped.iconPrev;
            delete mapped.iconNext;

            return mapped;
        },
    },
    mounted() {
        this.checkMobile();
        window.addEventListener('resize', this.checkMobile);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.checkMobile);
    },
    emits: ['change', 'update:modelValue'],
    methods: {
        checkMobile() {
            this.isMobile = window.innerWidth <= 768;
        },
        getAriaLabel(pageNum, currentPage) {
            // Don't add aria-label to ellipsis
            if (pageNum === '...') {
                return undefined;
            }

            // If it's the current page, use the current page label
            if (pageNum === currentPage) {
                return `${this.ariaLabels.current} ${pageNum}`;
            }

            // Otherwise use the regular page label
            return `${this.ariaLabels.page} ${pageNum}`;
        },
        getDisplayedPages(currentPage, pageCount) {
            const pages = [];

            // If we have 4 or fewer pages, show them all
            if (pageCount <= 4) {
                for (let i = 1; i <= pageCount; i++) {
                    pages.push(i);
                }
                return pages;
            }

            if (this.isMobile) {
                // Mobile: always show exactly 4 buttons + ellipsis
                // Current page is in the first 3 pages: show 1 2 3 ... last
                if (currentPage <= 3) {
                    for (let i = 1; i <= 3; i++) {
                        pages.push(i);
                    }
                    pages.push('...');
                    pages.push(pageCount);
                }
                // Current page is in the last 3 pages: show 1 ... last-2 last-1 last
                else if (currentPage > pageCount - 3) {
                    pages.push(1);
                    pages.push('...');
                    for (let i = pageCount - 2; i <= pageCount; i++) {
                        pages.push(i);
                    }
                }
                // Current page is in the middle: show 1 ... current current+1 ... last
                else {
                    pages.push(1);
                    pages.push('...');
                    pages.push(currentPage);
                    pages.push(currentPage + 1);
                    pages.push('...');
                    pages.push(pageCount);
                }
                return pages;
            }
            // Desktop: show more pages
            const maxVisiblePages = 3;

            // Current page is in the first 3 pages: show 1 2 3 ... last
            if (currentPage < maxVisiblePages) {
                for (let i = 1; i <= maxVisiblePages; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(pageCount);
            }
            // Current page is in the last 3 pages: show 1 ... last-2 last-1 last
            else if (currentPage > pageCount - maxVisiblePages) {
                pages.push(1);
                pages.push('...');
                for (let i = pageCount - (maxVisiblePages - 1); i <= pageCount; i++) {
                    pages.push(i);
                }
            }
            // Current page is in the middle: show 1 ... current-1 current current+1 ... last
            else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(pageCount);
            }

            return pages;
        },
        handlePageChange(pageIndex, slotProps) {
            this.$refs.paginator?.$el?.dispatchEvent(new Event('page'));

            if (slotProps.pageCallback) {
                slotProps.pageCallback({ page: pageIndex });
            }

            // Emit our own events
            this.onPrimeVuePage({ page: pageIndex });
        },
        onPrimeVuePage(event) {
            // PrimeVue uses 0-based index, convert to 1-based for consistency
            const page = event.page + 1;

            this.$emit('update:modelValue', page);
            this.$emit('change', page);
        },
    },
};
</script>

<style scoped lang="scss">
@import '~assets/scss/toolbox.scss';

/* Buefy-style pagination with square buttons - desktop: single row right-aligned */
.primevue-pagination {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    list-style: none;
    margin: 0;
    padding: 0.5rem 0;
    width: 100%;
}

/* Navigation row - prev/next buttons */
.pagination-nav-row {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
}

/* Page numbers row */
.pagination-pages-row {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
}

/* Page number buttons - square design */
.pagination-link {
    border: 1px solid $border;
    background-color: $white;
    color: $text;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 400;
    border-radius: $radius-small;
    transition: $transition-fast;
    text-decoration: none;
    user-select: none;
    flex-shrink: 0;
}

.pagination-link.is-current {
    background-color: var(--p-button-primary-background);
}

/* Chevron navigation buttons */
.pagination-chevron {
    border: 1px solid $border;
    background-color: $white;
    color: $text;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: $radius-small;
    transition: $transition-fast;
    user-select: none;
    flex-shrink: 0;
}

.pagination-chevron:hover:not([disabled]) {
    border-color: $primary;
    background-color: $grey-lightest;
}

.pagination-chevron[disabled] {
    background-color: $white-ter;
    border-color: $border;
    color: $grey-light;
    cursor: not-allowed;
    opacity: 0.6;
}

/* Icon inside chevron buttons */
.pagination-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
}

.pagination-icon i {
    line-height: 1;
}

/* Ellipsis styling - for disabled page buttons showing ... */
.pagination-link[disabled] {
    border: none;
    background: transparent;
    cursor: default;
    color: $grey-light;
    font-weight: 400;
}

/* Mobile optimizations - stack vertically with nav on top */
@media screen and (max-width: 768px) {
    .primevue-pagination {
        flex-direction: row-reverse;
        justify-content: center;
        gap: 0.5rem;
    }

    /* Reorder: navigation buttons on top */
    .pagination-nav-row {
        order: -1;
        gap: 0.25rem;
    }

    /* Page numbers below */
    .pagination-pages-row {
        order: 1;
        gap: 0.25rem;
    }

    .pagination-link,
    .pagination-chevron {
        width: 2.25rem;
        height: 2.25rem;
        font-size: 0.875rem;
        min-width: 2.25rem;
    }

    .pagination-icon {
        font-size: 1rem;
    }

    /* Make ellipsis take less space */
    .pagination-link[disabled] {
        width: 1.75rem;
        min-width: 1.75rem;
    }
}
</style>
